import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge, Button, Card, Input, Spinner } from '@/components/ui';
import { getCompanyOrgChart, type OrgChartNode } from '@/api/services/employment.service';
import { getErrorMessage } from '@/lib/getErrorMessage';

/** Depth cap keeps a malformed graph from rendering unboundedly. */
const MAX_DEPTH = 12;

function ChartNode({
  node,
  childrenByManager,
  depth,
  cycleUserIds,
}: {
  node: OrgChartNode;
  childrenByManager: Map<string, OrgChartNode[]>;
  depth: number;
  cycleUserIds: Set<string>;
}): React.ReactElement {
  const [expanded, setExpanded] = useState(depth < 2);
  const children = childrenByManager.get(node.userId) ?? [];
  const hasChildren = children.length > 0 && depth < MAX_DEPTH;

  return (
    <li>
      <div className="flex items-start gap-2 py-1.5">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            aria-expanded={expanded}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} reports of ${node.name}`}
            className="mt-0.5 rounded p-0.5 text-gray-500 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:text-gray-100"
          >
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
        ) : (
          <span className="w-5" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {node.name}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">{node.employeeId}</span>
            {cycleUserIds.has(node.userId) && (
              <Badge variant="danger">Reporting cycle</Badge>
            )}
            {node.directReportCount > 0 && (
              <Badge variant="neutral">{node.directReportCount} reports</Badge>
            )}
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300">
            {[node.jobTitleName, node.departmentName].filter(Boolean).join(' · ') ||
              'No assignment details'}
          </p>
        </div>
      </div>
      {hasChildren && expanded && (
        <ul className="ml-4 border-l border-gray-200 pl-3 dark:border-gray-700">
          {children.map((child) => (
            <ChartNode
              key={child.userId}
              node={child}
              childrenByManager={childrenByManager}
              depth={depth + 1}
              cycleUserIds={cycleUserIds}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export function CompanyOrgChart({
  companyId,
  embedded = false,
}: {
  companyId: string;
  /** Inside a company section that already provides the card. */
  embedded?: boolean;
}): React.ReactElement {
  const [search, setSearch] = useState('');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['hr-org-chart', companyId],
    queryFn: () => getCompanyOrgChart(companyId),
  });

  const { childrenByManager, roots, cycleUserIds } = useMemo(() => {
    const byManager = new Map<string, OrgChartNode[]>();
    for (const node of data?.nodes ?? []) {
      if (!node.managerUserId) continue;
      const siblings = byManager.get(node.managerUserId) ?? [];
      siblings.push(node);
      byManager.set(node.managerUserId, siblings);
    }
    return {
      childrenByManager: byManager,
      roots: (data?.nodes ?? []).filter((node) => node.managerUserId === null),
      cycleUserIds: new Set(data?.cycleUserIds ?? []),
    };
  }, [data]);

  const term = search.trim().toLowerCase();
  const matches = term
    ? (data?.nodes ?? []).filter(
        (node) =>
          node.name.toLowerCase().includes(term) ||
          node.employeeId.toLowerCase().includes(term)
      )
    : null;

  const nobodyHasManager =
    (data?.nodes?.length ?? 0) > 1 && childrenByManager.size === 0;
  const body = (
    <>
      {(data?.nodes?.length ?? 0) > 0 && (
        <div className="flex flex-wrap items-end justify-between gap-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {data ? `${data.total ?? data.nodes?.length ?? 0} people` : ''}
          </p>
          <div className="w-full max-w-xs">
            <Input
              id="org-chart-search"
              label="Find an employee"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name or employee ID"
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-10" role="status" aria-live="polite">
          <Spinner />
          <span className="sr-only">Loading organization chart</span>
        </div>
      ) : error ? (
        <div className="mt-4 space-y-3" role="alert">
          <p className="text-sm text-danger">{getErrorMessage(error)}</p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : !data || (data.nodes?.length ?? 0) === 0 ? (
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Nobody here yet. People appear once they are set up in People.
        </p>
      ) : (
        <>
          {(data.cycleUserIds?.length ?? 0) > 0 && (
            <p
              role="status"
              className="mt-4 rounded-lg bg-warning/10 px-3 py-2 text-sm text-warning-foreground dark:text-warning"
            >
              {data.cycleUserIds?.length ?? 0} employee(s) are in a reporting cycle and are shown at the
              top level until their reporting line is corrected.
            </p>
          )}
          {nobodyHasManager && !matches && (
            <p role="status" className="mt-4 text-sm text-gray-600 dark:text-gray-300">
              Nobody here has a manager set, so everyone shows at the top. Set a manager with a job
              change to build the chart.
            </p>
          )}
          {matches ? (
            <ul className="mt-4 space-y-1" aria-label="Search results">
              {matches.length === 0 ? (
                <li className="text-sm text-gray-600 dark:text-gray-300">
                  No employee matches this search.
                </li>
              ) : (
                matches.map((node) => (
                  <li key={node.userId} className="py-1.5">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {node.name}
                    </span>{' '}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {node.employeeId}
                    </span>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      {[node.jobTitleName, node.departmentName].filter(Boolean).join(' · ') ||
                        'No assignment details'}
                    </p>
                  </li>
                ))
              )}
            </ul>
          ) : (
            <ul className="mt-4">
              {roots.map((node) => (
                <ChartNode
                  key={node.userId}
                  node={node}
                  childrenByManager={childrenByManager}
                  depth={0}
                  cycleUserIds={cycleUserIds}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </>
  );
  return embedded ? <div className="p-4">{body}</div> : <Card>{body}</Card>;
}
